import {
  getVertexEnabledAjiogram,
  getVertexEnabledLuxe,
  getVertexEnabledForSis
} from '../selectors/featureFlagsSelector';
import { RootState } from '../types/rootState';

/** Amplitude payloads use booleans; some configs use 'ON' or string "true". */
export function isVertexExperimentFlagOn(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (value === 'ON' || value === 'on') return true;
  if (typeof value === 'string' && value.toLowerCase() === 'true') return true;
  return false;
}

/**
 * Returns vertex query params ({ vertexEnabled: true, visitorId }) for ajiogram/luxe stores
 * when the master flag and route-specific flag are both enabled.
 * Returns {} for all other stores or when flags are off.
 *
 * @param state       - Redux state
 * @param store       - Store name (e.g. 'ajiogram', 'luxe', 'ajio')
 * @param requestPath - Request pathname (e.g. '/search', '/category/shoes')
 * @param visitorId   - Visitor ID from cookie
 */
export function getVertexQueryParams(
  state: RootState,
  store = '',
  requestPath = '',
  visitorId = '',
  userEncryptedId = '',
  routeType = ''
): { vertexEnabled?: boolean; visitorId?: string; userEncryptedId?: string } {
  const normalizedStore = store.toLowerCase();

  if (!['ajiogram', 'luxe'].includes(normalizedStore)) {
    const { flags = {} } = (state as any) || {};
    if (getVertexEnabledForSis(state)) {
      const childStores: string[] = (flags?.childStores || []).map((s: string) => s.toLowerCase());
      if (childStores.includes(normalizedStore)) {
        return {
          vertexEnabled: true,
          ...(visitorId && { visitorId }),
          ...(userEncryptedId && { userEncryptedId })
        };
      }
    }
    return {};
  }

  const vertexEnabledAjiogram = getVertexEnabledAjiogram(state);
  const vertexEnabledLuxe = getVertexEnabledLuxe(state);
  const storeConfig = normalizedStore === 'ajiogram' ? vertexEnabledAjiogram : vertexEnabledLuxe;

  if (!storeConfig?.master) {
    return {};
  }

  const isSearchRoute = routeType === 'search' || (!routeType && (requestPath.includes('/search') || requestPath.includes('/find')));
  const isBrowseRoute = routeType === 'browse' || (!routeType && (requestPath.includes('/category') || requestPath.includes('/widgets') || requestPath?.includes('/s1/') || requestPath?.includes('/s/')));

  const isEnabled =
    (isSearchRoute && storeConfig?.vertexSearchEnabled) ||
    (isBrowseRoute && storeConfig?.vertexBrowseEnabled);

  if (!isEnabled) {
    return {};
  }

  return {
    vertexEnabled: true,
    ...(visitorId && { visitorId }),
    ...(userEncryptedId && { userEncryptedId })
  };
}

/**
 * Gets vertex store scope configuration based on request path
 * @param store - Store name
 * @param requestPath - Request path (e.g., '/suggestion', '/search', '/category', '/widget')
 * @returns Whether vertex store scope is enabled
 */
export function getVertexStoreScopeEnabled(
  state: RootState,
  store = '',
  requestPath = '' // kept for API compatibility
): boolean {
  void requestPath;
  const normalizedStore = store.toLowerCase();
  const { flags = {} } = (state as any) || {};

  if (normalizedStore === 'ajiogram') {
    return !!getVertexEnabledAjiogram(state)?.master;
  }

  if (normalizedStore === 'luxe') {
    return !!getVertexEnabledLuxe(state)?.master;
  }

  const isVertexEnabled = (flags?.vertexEnabled || 'OFF') === 'ON';
  const isVertexEnabledForSis = !!flags?.vertexEnabledForSis;
  const childStores: string[] = (flags?.childStores || []).map((s: string) => s.toLowerCase());
  if (['', 'ajio', 'rilfnl'].includes(normalizedStore)) return isVertexEnabled;
  return isVertexEnabledForSis && childStores.includes(normalizedStore);
}
